import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, RefreshCcw, AlertTriangle, Calendar, Activity } from "lucide-react";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { getJSON, postJSON } from "../lib/api";
import { useAuthStore } from "../state/authStore";
import { AuditResultView } from "./PublicAuditTool";

/** /tools/public-audit/:scanId — public deep-link to an existing scan.
 * Same auth rules as the API: owner OR anon-from-same-IP can view. */
export default function PublicAuditResult() {
    const { scanId } = useParams();
    const user = useAuthStore((s) => s.user);
    const [scan, setScan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rerunning, setRerunning] = useState(false);

    const loadScan = async () => {
        setLoading(true);
        setError(null);
        try {
            let d = await getJSON(`/audits/${encodeURIComponent(scanId)}`);
            // If still pending, poll for up to ~25s.
            if (d.status === "pending" || d.status === "running") {
                for (let i = 0; i < 20; i++) {
                    await new Promise((r) => setTimeout(r, 1500));
                    d = await getJSON(`/audits/${encodeURIComponent(scanId)}`);
                    if (d.status === "complete" || d.status === "failed") break;
                }
            }
            setScan(d);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) setError("not_found");
            else if (status === 403) setError("forbidden");
            else setError("load_error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadScan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanId]);

    const handleRerun = async () => {
        if (!scan?.domain || rerunning) return;
        setRerunning(true);
        try {
            const created = await postJSON("/audits", { domain: scan.domain });
            window.location.assign(`/tools/public-audit/${created.scan_id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "object" ? detail.message || JSON.stringify(detail) : detail || e.message;
            setError("rerun:" + msg);
            setRerunning(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-text-muted" data-testid="audit-loading">
                <Loader2 className="w-5 h-5 animate-spin text-accent-cyan mr-2" />
                Loading audit report…
            </div>
        );
    }

    if (error === "not_found" || error === "forbidden") {
        return (
            <main className="pt-24 pb-20 mx-auto max-w-3xl px-5 md:px-10 text-center" data-testid="audit-not-accessible">
                <SEO title={error === "not_found" ? "Audit not found" : "Audit not accessible"} noindex />
                <h1 className="font-display text-3xl text-text-primary">
                    {error === "not_found"
                        ? "This audit report doesn't exist or was removed."
                        : "This audit belongs to another account."}
                </h1>
                <Link to="/tools/public-audit" className="inline-flex items-center gap-1.5 mt-6 text-accent-cyan hover:underline">
                    <ArrowLeft className="w-4 h-4" /> Run your own audit
                </Link>
            </main>
        );
    }

    if (error && error.startsWith("rerun:")) {
        // Show the error but keep the existing scan visible.
    }

    return (
        <main className="pt-24 md:pt-28 pb-20 mx-auto max-w-5xl px-5 md:px-10" data-testid="audit-result-page">
            <SEO
                title={`Public Surface Audit · ${scan?.domain || ""}`}
                description={`Public Surface Audit for ${scan?.domain || "this domain"}: subdomains discovered, risk score, attack-surface findings.`}
                canonicalUrl={`${SITE_DOMAIN}/tools/public-audit/${scanId}`}
                noindex
            />
            <div className="flex items-center justify-between mb-6">
                <Link
                    to="/tools/public-audit"
                    className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wider text-text-dim hover:text-accent-cyan transition-colors"
                    data-testid="audit-back-link"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Public Surface Audit
                </Link>
                {scan?.domain && (
                    <button
                        onClick={handleRerun}
                        disabled={rerunning}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 text-[12px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors disabled:opacity-50"
                        data-testid="audit-rerun-btn"
                    >
                        {rerunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                        Re-run scan
                    </button>
                )}
            </div>

            <header className="mb-6">
                <h1 className="font-display font-semibold text-3xl text-text-primary">
                    Audit of <span className="font-mono text-accent-cyan">{scan?.domain}</span>
                </h1>
                <p className="text-[12px] font-mono text-text-dim mt-1 inline-flex flex-wrap items-center gap-2">
                    <Activity className="w-3 h-3" /> scan_id {scan?.scan_id}
                    {scan?.created_at && (
                        <>
                            <span>·</span>
                            <Calendar className="w-3 h-3" />
                            {new Date(scan.created_at).toLocaleString()}
                        </>
                    )}
                    <span>·</span>
                    <span className="uppercase tracking-wider">{scan?.subscription_tier || "anon"} tier</span>
                </p>
            </header>

            {error && error.startsWith("rerun:") && (
                <div className="mb-4 flex items-start gap-2 text-[12.5px] text-accent-amber bg-accent-amber/[0.05] border border-accent-amber/30 rounded-md p-3">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{error.replace("rerun:", "")}</span>
                </div>
            )}

            {scan?.status === "failed" && (
                <div className="rounded-md border border-accent-red/30 bg-accent-red/[0.05] p-4 text-[13px] text-accent-red mb-6" data-testid="audit-failed">
                    Scan failed: {scan.error || "unknown error"}.
                </div>
            )}

            {scan && scan.status !== "failed" && <AuditResultView scan={scan} anon={!user} />}
        </main>
    );
}
